import { useRef, useEffect, useCallback, useState } from 'react'
import Story from './Story'

interface Props {
  stories: Story[] | null
  onRead: (id: StoryId) => void
  onSave: (id: StoryId) => void
  onUnsave: (id: StoryId) => void
}

export default function StoryList({ stories, onRead, onSave, onUnsave }: Props) {
  const containerRef = useRef<HTMLDivElement>(null)
  const observerRef = useRef<IntersectionObserver | null>(null)
  const elementToIdRef = useRef<Map<Element, StoryId>>(new Map())
  const onReadRef = useRef(onRead)
  onReadRef.current = onRead

  // Disable scroll-to-read on mobile (viewport < 768px)
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768)

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const getObserver = useCallback(() => {
    if (observerRef.current) {
      return observerRef.current
    }
    const container = containerRef.current
    if (!container) {
      return null
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          // Story scrolled past top of container
          if (!entry.isIntersecting && entry.boundingClientRect.top < 0) {
            const storyId = elementToIdRef.current.get(entry.target)
            if (storyId !== undefined) {
              onReadRef.current(storyId)
              observer.unobserve(entry.target)
              elementToIdRef.current.delete(entry.target)
            }
          }
        }
      },
      { root: container },
    )
    observerRef.current = observer
    return observer
  }, [])

  useEffect(() => {
    return () => observerRef.current?.disconnect()
  }, [])

  const registerForReadOnScroll = useCallback(
    (element: HTMLElement, storyId: StoryId) => {
      const observer = getObserver()
      if (!observer) {
        return
      }
      elementToIdRef.current.set(element, storyId)
      observer.observe(element)
    },
    [getObserver],
  )

  const unregisterForReadOnScroll = useCallback((element: HTMLElement) => {
    observerRef.current?.unobserve(element)
    elementToIdRef.current.delete(element)
  }, [])

  if (stories === null) {
    return (
      <div id="articles">
        <article className="last">Loading...</article>
      </div>
    )
  }

  return (
    <div id="articles" ref={containerRef} className="span9">
      {stories.map((story) => (
        <Story
          key={story.id}
          story={story}
          onRead={onRead}
          onSave={onSave}
          onUnsave={onUnsave}
          registerForReadOnScroll={isMobile ? undefined : registerForReadOnScroll}
          unregisterForReadOnScroll={isMobile ? undefined : unregisterForReadOnScroll}
        />
      ))}
      <article className="last">No more stories</article>
    </div>
  )
}
